import React from 'react';

import cn from 'classnames';

import { Icon, Popup, UiDropdown, UiText } from '@shared/ui';

import cls from './EditWarehouse.module.css';
import { MoveWarehouseDropdownContent } from './MoveWarehouseDropdownContent';
import { useEditWarehouse } from '../model/useEditWarehouse';

import type { MoveWarehouseListItem } from '../model/types';
import type { Warehouse } from '@entities/equipment';

interface EditWarehouseProps {
    classes?: string;
    canRename?: boolean;
    canMove?: boolean;
    hasFilterValue?: boolean;
    warehouses?: MoveWarehouseListItem[];
    warehouseId: number;
    warehouseTitle: string;
    itemsCount: number;
    inventoryItems?: Warehouse['items'];
    onRenameClick?: () => void;
}

export const EditWarehouse = ({
    classes,
    canRename = true,
    canMove = true,
    hasFilterValue = false,
    warehouses = [],
    warehouseId,
    itemsCount,
    inventoryItems = [],
    onRenameClick,
}: EditWarehouseProps) => {
    const {
        t,
        isMoveMode,
        isDeleteAlertOpen,
        handleOpenChange,
        openMoveMode,
        closeDeleteAlert,
        deleteWarehouse,
        moveInventory,
    } = useEditWarehouse({
        warehouseId,
        itemsCount,
        inventoryGuids: inventoryItems.map((item) => item.guid),
        hasFilterValue,
    });

    return (
        <>
            <div className={cn(cls.EditWarehouse, classes)}>
                <UiDropdown
                    placement="right-start"
                    className={cls.menuItems}
                    onOpenChange={handleOpenChange}
                    renderTrigger={({ ref, referenceProps }) => (
                        <button
                            ref={ref}
                            type="button"
                            className={cls.iconWrapper}
                            {...referenceProps}
                        >
                            <Icon iconName="horizontalDots" />
                        </button>
                    )}
                >
                    {({ close }) =>
                        isMoveMode ? (
                            <MoveWarehouseDropdownContent
                                warehouses={warehouses}
                                onClose={close}
                                onWarehouseClick={(warehouse) => moveInventory(warehouse, close)}
                            />
                        ) : (
                            <UiDropdown.List>
                                {canRename && (
                                    <UiDropdown.ListItem
                                        className={cls.menuItem}
                                        data-id="warehouse-rename-button"
                                        onClick={onRenameClick}
                                    >
                                        <UiText variant="subPlain">
                                            {t(
                                                'EditWarehouseModal.return.QuickActionButton.rename'
                                            )}
                                        </UiText>
                                    </UiDropdown.ListItem>
                                )}
                                {canMove && (
                                    <UiDropdown.ListItem
                                        closeOnClick={false}
                                        className={cls.menuItem}
                                        data-id="move-all-inventory-button"
                                        onClick={openMoveMode}
                                    >
                                        <UiText variant="subPlain">
                                            {t(
                                                hasFilterValue
                                                    ? 'EditWarehouseModal.return.QuickActionButton.moveSelected'
                                                    : 'EditWarehouseModal.return.QuickActionButton.moveAll'
                                            )}
                                        </UiText>
                                    </UiDropdown.ListItem>
                                )}
                                <UiDropdown.ListItem
                                    className={cn(cls.menuItem, cls.deleteItem)}
                                    data-id="delete-warehouse-button"
                                    onClick={() => {
                                        void deleteWarehouse();
                                    }}
                                >
                                    <UiText variant="subPlain">
                                        {t('EditWarehouseModal.return.QuickActionButton.delete')}
                                    </UiText>
                                </UiDropdown.ListItem>
                            </UiDropdown.List>
                        )
                    }
                </UiDropdown>
            </div>
            <Popup
                isOpen={isDeleteAlertOpen}
                type="alert"
                contentText={t('EditWarehouseModal.deleteWarehouseCallback.text')}
                alertBtnText={t('EditWarehouseModal.deleteWarehouseCallback.button.text')}
                onHidePopup={closeDeleteAlert}
            />
        </>
    );
};
